import { useContext } from 'react';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ScreenSizeContext } from '@wix/da-hooks/pkg/useMeasureElement/ScreenSizeContext';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import isArray from 'lodash/isArray';
import { type ElementPlacementMap, WindowBreakpoint } from './types';

type UseElementPlacementForAreaReturnType<
  ElementPlacementEnum,
  ElementPlacementMapType extends ElementPlacementMap<ElementPlacementEnum>,
> = {
  [key in keyof Omit<
    ElementPlacementMapType,
    'elementGroups' | 'meta'
  >]: boolean;
} & {
  elementGroups: {
    [key in keyof ElementPlacementMapType['elementGroups']]: Omit<
      ElementPlacementMapType['elementGroups'][key],
      'groupPlacement'
    > & { groupPlacement: boolean };
  };
} & Pick<ElementPlacementMapType, 'meta'>;

/**
 *
 */
export function useElementPlacementForArea<
  ElementPlacementEnum,
  ElementPlacementMapType extends ElementPlacementMap<ElementPlacementEnum>,
>(
  placementArea: ElementPlacementEnum,
  context: React.Context<ElementPlacementMapType>
): UseElementPlacementForAreaReturnType<
  ElementPlacementEnum,
  ElementPlacementMapType
> {
  const elementPlacementMap = useContext(context);
  const mapWithBooleansInstead = mapValues(
    omit(elementPlacementMap, ['elementGroups', 'meta']),
    placement => {
      return isArray(placement)
        ? placement.includes(placementArea)
        : placement === placementArea;
    }
  );

  return {
    ...mapWithBooleansInstead,
    elementGroups: mapValues(
      elementPlacementMap.elementGroups,
      elementGroup => ({
        ...elementGroup,
        groupPlacement: elementGroup.groupPlacement === placementArea,
      })
    ),
    meta: elementPlacementMap.meta,
  } as UseElementPlacementForAreaReturnType<
    ElementPlacementEnum,
    ElementPlacementMapType
  >;
}

// TODO: Expand with other common breakpoints
export function useWindowBreakpoint(): WindowBreakpoint {
  const isMobile = useContext(MobileContext);
  const screenSize = useContext(ScreenSizeContext);
  if (isMobile) {
    return WindowBreakpoint.MobileAverageSize;
  }

  if (screenSize.width >= 1100) {
    return WindowBreakpoint.DesktopAverageSize;
  }
  return WindowBreakpoint.DesktopSmallSize;
}
